<script setup lang="ts">
const colorMode = useColorMode()
const isDark = computed({
  get() {
    return colorMode.value === 'dark'
  },
  set(_isDark) {
    colorMode.preference = _isDark ? 'dark' : 'light'
  },
})

const router = useRouter()
const [navOpen, toggleNav] = useToggle()
router.afterEach(() => {
  navOpen.value = false
})

const asideRef = templateRef('asideRef')
onClickOutside(asideRef, () => {
  navOpen.value = false
})
</script>

<template>
  <AuthState v-slot="{ loggedIn, user }">
    <div class="default-layout bg-muted">
      <aside
        ref="asideRef"
        class="z-100 flex flex-col border-r border-default bg-default md:z-0"
        :class="{ open: navOpen }"
      >
        <header class="flex h-16 items-center p-2">
          <RialticLogo class="h-8 dark:text-white" />
          <div class="px-2">
            <UBadge color="primary">Admin</UBadge>
          </div>
        </header>

        <div class="flex-1">
          <NavMenu />
        </div>

        <footer
          class="flex items-center justify-between border-t border-default p-2"
        >
          <UButton
            :icon="isDark ? 'i-ph-moon' : 'i-ph-sun'"
            variant="ghost"
            @click="isDark = !isDark"
          />

          <NuxtLink v-if="!loggedIn" to="/auth/auth0" external>Login</NuxtLink>

          <UButton
            to="/profile"
            :label="user?.name"
            color="neutral"
            size="md"
            trailing
            variant="ghost"
          >
            <template #trailing>
              <UAvatar :src="user?.picture" size="sm" />
            </template>
          </UButton>
        </footer>
      </aside>

      <header
        class="flex items-center justify-start border-b border-default bg-default p-2 lg:hidden"
      >
        <UButton
          color="neutral"
          icon="i-ph-list"
          variant="ghost"
          @click="toggleNav()"
        />
      </header>

      <main>
        <slot />
      </main>
    </div>
  </AuthState>
</template>

<style scoped>
.default-layout {
  display: grid;
  position: relative;
  grid-template-columns: 0px 1fr;
  grid-template-rows: 40px 1fr;
  grid-template-areas:
    'nav header'
    'nav content';

  height: 100vh;
  width: 100%;
}

aside {
  grid-area: nav;
  width: 256px;
  transform: translateX(-256px);
  transition: transform 0.3s ease;
}

aside.open {
  transform: translateX(0);
  z-index: 10;
}

@media (min-width: 1024px) {
  .default-layout {
    grid-template-columns: 256px 1fr;
    grid-template-rows: auto 1fr;
    grid-template-areas:
      'nav content'
      'nav content';
  }

  aside {
    transform: translateX(0);
  }
}

header {
  grid-area: header;
}

main {
  grid-area: content;
  overflow: auto;
}
</style>
